import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Users, Upload, ShieldAlert, ListFilter, Save, Trash2, Download,
  Sparkles, FileWarning, BadgeAlert, Tag, Check, AlertTriangle,
  FileText, Plus, Search, Mail, ChevronUp, ChevronDown, ChevronsUpDown,
} from "lucide-react";

const TAG_OPTIONS = [
  { value: "", label: "— No tag —" },
  { value: "supporter", label: "Supporter" },
  { value: "undecided", label: "Undecided" },
  { value: "opposed", label: "Opposed" },
  { value: "no_answer", label: "No answer" },
  { value: "dnc", label: "Do not contact" },
];
// Pre-filtered subset used in the filter pills — recomputed once at module load
// so we don't redo the filter on every render of <FilterFields>.
const TAG_FILTER_PILLS = TAG_OPTIONS.filter((o) => o.value);
const PROPENSITY_OPTIONS = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];
const PARTY_OPTIONS = [
  { value: "any", label: "Any party" },
  { value: "R", label: "Republican (R)" },
  { value: "D", label: "Democrat (D)" },
  { value: "I", label: "Independent (I)" },
  { value: "O", label: "Other / Minor" },
  { value: "", label: "Unaffiliated / Blank" },
];

const TAG_STYLES = {
  supporter: "bg-green-100 text-green-900",
  undecided: "bg-amber-100 text-amber-900",
  opposed: "bg-red-100 text-red-900",
  no_answer: "bg-orange-100 text-orange-900",
  dnc: "bg-slate-200 text-slate-800",
};
const PROPENSITY_STYLES = {
  high: "bg-emerald-100 text-emerald-900",
  medium: "bg-sky-100 text-sky-900",
  low: "bg-slate-100 text-slate-700",
};

const emptyFilter = {
  party: "any",
  town: "",
  precinct: "",
  name_contains: "",
  age_min: "",
  age_max: "",
  tags: [],
  propensity: [],
  voted_min: "",
  recent_window: "",
  q: "",
  vbm: "any",  // 'any' | 'yes' | 'no'
  has_notes: false,
};

function toApiFilter(f, bookId) {
  const out = {};
  if (f.party && f.party !== "any") out.party = f.party;
  if (f.party === "") out.party = "";
  if (f.town?.trim()) out.town = f.town.trim();
  if (f.precinct?.trim()) out.precinct = f.precinct.trim();
  if (f.name_contains?.trim()) out.name_contains = f.name_contains.trim();
  if (f.q?.trim()) out.q = f.q.trim();
  if (f.vbm === "yes") out.vbm = true;
  if (f.vbm === "no") out.vbm = false;
  if (f.has_notes) out.has_notes = true;
  if (f.age_min !== "" && f.age_min != null) out.age_min = Number(f.age_min);
  if (f.age_max !== "" && f.age_max != null) out.age_max = Number(f.age_max);
  if (f.tags?.length) out.tags = f.tags;
  if (f.propensity?.length) out.propensity = f.propensity;
  if (f.voted_min !== "" && f.voted_min != null) out.voted_min = Number(f.voted_min);
  if (f.recent_window !== "" && f.recent_window != null) out.recent_window = Number(f.recent_window);
  if (bookId) out.book_id = bookId;
  return out;
}

// Tri-state VBM filter value coming back from the API.
const vbmFromApi = (v) => {
  if (v === true) return "yes";
  if (v === false) return "no";
  return "any";
};

// Commit-button label for the upload dialog.
const importCommitLabel = (importing, importMode) => {
  if (importing) return "Importing…";
  if (importMode === "replace") return "Replace voter list";
  return "Append to voter list";
};

function fromApiFilter(api_f = {}) {
  return {
    party: api_f.party ?? "any",
    town: api_f.town || "",
    precinct: api_f.precinct || "",
    name_contains: api_f.name_contains || "",
    age_min: api_f.age_min ?? "",
    age_max: api_f.age_max ?? "",
    tags: api_f.tags || [],
    propensity: api_f.propensity || [],
    voted_min: api_f.voted_min ?? "",
    recent_window: api_f.recent_window ?? "",
    q: api_f.q || "",
    vbm: api_f.vbm === true ? "yes" : api_f.vbm === false ? "no" : "any",
    has_notes: !!api_f.has_notes,
  };
}

export default function VoterList() {
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const [books, setBooks] = useState([]);            // [{id,name,is_default,voter_count}]
  const [activeBookId, setActiveBookId] = useState(null);
  const [walkBusy, setWalkBusy] = useState(false);

  const [filter, setFilter] = useState(emptyFilter);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [loading, setLoading] = useState(false);

  // Profile dialog state
  const [profileVoter, setProfileVoter] = useState(null);  // currently-open voter
  const [profileBusy, setProfileBusy] = useState(false);

  const [savedLists, setSavedLists] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  const [aiQuery, setAiQuery] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiExplain, setAiExplain] = useState("");

  // Upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [parseRes, setParseRes] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [importMode, setImportMode] = useState("replace");
  const [importing, setImporting] = useState(false);
  const fileInput = useRef(null);

  const refreshMeta = async () => {
    try {
      const [m, s, b] = await Promise.all([
        api.get("/voters/privacy-ack"),
        api.get("/voters/stats"),
        api.get("/voters/books"),
      ]);
      setMeta(m.data);
      setStats(s.data);
      const bookList = b.data || [];
      setBooks(bookList);
      if (!activeBookId && bookList.length) {
        const def = bookList.find((x) => x.is_default) || bookList[0];
        setActiveBookId(def.id);
      }
      if (!m.data.accepted) setShowPrivacy(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const fetchRows = async (
    nextPage = page,
    nextFilter = filter,
    bookId = activeBookId,
    nextSortBy = sortBy,
    nextSortDir = sortDir,
    nextPageSize = pageSize,
  ) => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `/voters/query?page=${nextPage}&page_size=${nextPageSize}&sort_by=${encodeURIComponent(nextSortBy)}&sort_dir=${nextSortDir}`,
        toApiFilter(nextFilter, bookId),
      );
      setRows(data.rows || []);
      setTotal(data.approx_total || 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Sort handler — clicking a column header. Toggles asc/desc when the same
  // column is clicked, otherwise sets asc on the new column. Resets to page 1
  // because the result order changes entirely.
  const onSort = (col) => {
    let nextDir = "asc";
    if (sortBy === col) nextDir = sortDir === "asc" ? "desc" : "asc";
    setSortBy(col);
    setSortDir(nextDir);
    setPage(1);
    fetchRows(1, filter, activeBookId, col, nextDir, pageSize);
  };

  const onPageSizeChange = (size) => {
    const n = parseInt(size, 10) || 50;
    setPageSize(n);
    setPage(1);
    fetchRows(1, filter, activeBookId, sortBy, sortDir, n);
  };

  const fetchSavedLists = async () => {
    try {
      const { data } = await api.get("/voters/lists");
      setSavedLists(data || []);
    } catch (err) {
      // non-fatal — leave the saved-lists panel empty if this fails
      console.debug("voters/lists fetch failed", err);
    }
  };

  useEffect(() => {
    refreshMeta();
    fetchSavedLists();
    fetchRows(1, emptyFilter);
    // Mount-once bootstrap: fetch* / emptyFilter are stable enough that
    // adding them re-runs the whole load on every render. Intentional.
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // ----- Filtering -----
  const applyFilter = (nextFilter = filter) => {
    setPage(1);
    fetchRows(1, nextFilter);
  };

  const clearFilter = () => {
    setFilter(emptyFilter);
    setAiExplain("");
    setPage(1);
    fetchRows(1, emptyFilter);
  };

  const runAi = async () => {
    const q = aiQuery.trim();
    if (!q) return;
    setAiBusy(true);
    setAiExplain("");
    try {
      const { data } = await api.post("/voters/ai-filter", { query: q });
      const nextFilter = fromApiFilter(data.filter || {});
      setFilter(nextFilter);
      setAiExplain(data.explanation || "");
      applyFilter(nextFilter);
      toast.success("Filter built from your description");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setAiBusy(false);
    }
  };

  // ----- Tag save (notes moved to a dedicated endpoint) -----
  const patchVoter = async (voter, body) => {
    try {
      const { data } = await api.patch(`/voters/${voter.id}`, body);
      setRows((prev) => prev.map((r) => (r.id === voter.id ? { ...r, ...data } : r)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  // Prepend the new note into the row's local notes array (server sorts
  // most-recent first). No full refetch — keeps the row in place while
  // the caller types the next entry.
  const handleNoteAdded = (voter, note) => {
    setRows((prev) => prev.map((r) => {
      if (r.id !== voter.id) return r;
      const existing = Array.isArray(r.notes) ? r.notes : [];
      return { ...r, notes: [note, ...existing] };
    }));
  };

  // ----- Saved lists -----
  const saveList = async () => {
    const name = saveName.trim();
    if (!name) {
      toast.error("Give the list a name.");
      return;
    }
    try {
      await api.post("/voters/lists", { name, filters: toApiFilter(filter, activeBookId) });
      setSaveOpen(false);
      setSaveName("");
      fetchSavedLists();
      toast.success(`Saved "${name}"`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const applySavedList = (l) => {
    const next = fromApiFilter(l.filters);
    setFilter(next);
    applyFilter(next);
  };

  const deleteSavedList = async (l) => {
    try {
      await api.delete(`/voters/lists/${l.id}`);
      fetchSavedLists();
      toast.success("Removed saved list");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  // ----- Export CSV -----
  const exportCsv = async (listName = "all") => {
    try {
      const res = await api.post(
        `/voters/export?list_name=${encodeURIComponent(listName)}`,
        toApiFilter(filter, activeBookId),
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      const safe = (listName || "all").replace(/[^A-Za-z0-9_-]+/g, "-").toLowerCase();
      a.download = `electable-voter-list-${safe}-${today}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  // ----- Privacy ack -----
  const acceptPrivacy = async () => {
    try {
      await api.post("/voters/privacy-ack", { accepted: true });
      setShowPrivacy(false);
      refreshMeta();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  // ----- Upload flow -----
  const startUpload = () => {
    if (!meta?.accepted) {
      setShowPrivacy(true);
      return;
    }
    setUploadFile(null);
    setParseRes(null);
    setMapping(null);
    setImportMode("replace");
    setUploadOpen(true);
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFile(file);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/voters/parse-headers", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParseRes(data);
      setMapping({
        name: data.suggested_mapping.name || "",
        first_name: data.suggested_mapping.first_name || "",
        last_name: data.suggested_mapping.last_name || "",
        party: data.suggested_mapping.party || "",
        age: data.suggested_mapping.age || "",
        town: data.suggested_mapping.town || "",
        address: data.suggested_mapping.address || "",
        precinct: data.suggested_mapping.precinct || "",
        phone: data.suggested_mapping.phone || "",
        state_voter_id: data.suggested_mapping.state_voter_id || "",
        vbm: data.suggested_mapping.vbm || "",
        elections: data.suggested_mapping.elections || [],
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setUploadFile(null);
    }
  };

  const toggleElectionCol = (col) => {
    setMapping((m) => ({
      ...m,
      elections: m.elections.includes(col)
        ? m.elections.filter((c) => c !== col)
        : [...m.elections, col],
    }));
  };

  const commitImport = async () => {
    const hasFullName = !!mapping?.name;
    const hasSplitName = !!(mapping?.first_name && mapping?.last_name);
    if (!uploadFile || !(hasFullName || hasSplitName)) {
      toast.error("Map a Full name column, or map both First name and Last name.");
      return;
    }
    setImporting(true);
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("plan_json", JSON.stringify({ mapping, mode: importMode, book_id: activeBookId }));
    try {
      const { data } = await api.post("/voters/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Imported — ${data.added} added, ${data.merged} merged, ${data.skipped} skipped`,
      );
      setUploadOpen(false);
      refreshMeta();
      fetchRows(1, filter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setImporting(false);
    }
  };

  // ----- Walk Book -----
  const downloadWalkBook = async () => {
    setWalkBusy(true);
    try {
      const res = await api.post(
        "/voters/walk-book",
        toApiFilter(filter, activeBookId),
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `walk-book-${today}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Walk book downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setWalkBusy(false);
    }
  };

  // ----- Book CRUD -----
  const createBook = async () => {
    const name = window.prompt("Name this new voter list:");
    if (!name?.trim()) return;
    try {
      const { data } = await api.post("/voters/books", { name: name.trim() });
      await refreshMeta();
      setActiveBookId(data.id);
      setFilter(emptyFilter);
      setPage(1);
      fetchRows(1, emptyFilter, data.id);
      toast.success(`Created "${data.name}"`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const renameActiveBook = async () => {
    const b = books.find((x) => x.id === activeBookId);
    if (!b) return;
    const name = window.prompt("Rename this list to:", b.name);
    if (!name?.trim()) return;
    try {
      await api.patch(`/voters/books/${b.id}`, { name: name.trim() });
      refreshMeta();
      toast.success("Renamed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteActiveBook = async () => {
    const b = books.find((x) => x.id === activeBookId);
    if (!b || b.is_default) {
      toast.error("The default list can't be deleted. Rename it instead.");
      return;
    }
    if (!window.confirm(`Delete "${b.name}" and all ${b.voter_count} voters in it? This cannot be undone.`)) return;
    try {
      await api.delete(`/voters/books/${b.id}?confirm=DELETE`);
      const fresh = (await api.get("/voters/books")).data || [];
      setBooks(fresh);
      const def = fresh.find((x) => x.is_default) || fresh[0];
      setActiveBookId(def?.id || null);
      setFilter(emptyFilter);
      setPage(1);
      fetchRows(1, emptyFilter, def?.id || null);
      toast.success("List deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const switchBook = (id) => {
    setActiveBookId(id);
    setFilter(emptyFilter);
    setPage(1);
    fetchRows(1, emptyFilter, id);
  };

  // ----- Render -----
  const partyChart = useMemo(() => {
    if (!stats?.by_party) return [];
    return Object.entries(stats.by_party)
      .filter(([, n]) => n > 0)
      .sort((a, b) => b[1] - a[1]);
  }, [stats]);

  // Total of voters with any tag — surfaced in the stats strip. Memoized so
  // the reduce() doesn't re-run on every render.
  const taggedTotal = useMemo(
    () => Object.values(stats?.by_tag || {}).reduce((s, n) => s + n, 0),
    [stats],
  );

  const propensityChart = useMemo(() => {
    if (!stats?.by_propensity) return [];
    return ["high", "medium", "low"].map((k) => [k, stats.by_propensity[k] || 0]);
  }, [stats]);

  const tagChart = useMemo(() => {
    if (!stats?.by_tag) return [];
    return ["supporter", "undecided", "opposed", "dnc"].map((k) => [
      k, stats.by_tag[k] || 0,
    ]);
  }, [stats]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Voter List</p>
            <span data-testid="voter-pro-badge" className="inline-flex items-center text-[10px] font-bold uppercase tracking-[0.16em] bg-flag-red text-white px-2 py-0.5 rounded-full">Pro</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">Your voter file</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Upload a voter file from your county board of elections, tag voters as you canvass, and pull walk / call lists. Electable never sources or provides voter data — you bring your own.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {books.length > 0 && (
            <Select value={activeBookId || ""} onValueChange={switchBook}>
              <SelectTrigger className="h-10 min-w-[200px]" data-testid="voter-book-select">
                <SelectValue placeholder="Select list" />
              </SelectTrigger>
              <SelectContent>
                {books.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name} {b.is_default ? "(default)" : ""}  ·  {b.voter_count}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={createBook} data-testid="voter-book-new-btn" className="h-10 border-2 font-semibold">
            <Plus className="h-4 w-4 mr-1" /> New list
          </Button>
          {books.find((b) => b.id === activeBookId && !b.is_default) && (
            <Button variant="ghost" onClick={renameActiveBook} className="h-10 text-slate-600 hover:text-flag-navy" data-testid="voter-book-rename-btn">
              Rename
            </Button>
          )}
          {books.find((b) => b.id === activeBookId && !b.is_default) && (
            <Button variant="ghost" onClick={deleteActiveBook} className="h-10 text-flag-red" data-testid="voter-book-delete-btn">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button
            onClick={downloadWalkBook}
            disabled={walkBusy || total === 0}
            data-testid="voter-walkbook-btn"
            className="h-11 px-5 bg-flag-navy hover:bg-flag-navy-2 text-white font-bold"
            title="Generate a printable walk book (PDF) for canvassers"
          >
            <FileText className="h-4 w-4 mr-1.5" /> {walkBusy ? "Building…" : "Walk Book"}
          </Button>
          <Button
            onClick={startUpload}
            data-testid="voter-upload-open-btn"
            className="h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
          >
            <Upload className="h-4 w-4 mr-1.5" /> Upload CSV
          </Button>
        </div>
      </div>

      {/* Privacy banner — always visible */}
      <div
        data-testid="voter-privacy-banner"
        className="mt-6 flex items-start gap-3 bg-amber-50 border-2 border-amber-300 rounded-xl px-4 py-3"
      >
        <ShieldAlert className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-900 leading-relaxed">
          <strong className="font-bold">Private to your campaign.</strong> The voter data on this page is real people's personal information, isolated per account, and visible only to you. Use it strictly for lawful election activity (canvassing, GOTV, voter outreach). Do not share, sell, or repurpose it. Re-uploading replaces or appends to <em>your</em> data only — never another campaign's.
        </div>
      </div>

      {/* Stats strip — held back until the initial meta fetch resolves so we
          don't flash the "EmptyHero" before we know whether the user has
          uploaded voters. Guards instead of a chained ternary: exactly one
          of skeleton / strip / empty-hero renders. */}
      {meta === null && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="voter-stats-skeleton" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={`stat-skel-${i}`} className="bg-white border border-slate-200 rounded-xl p-5 animate-pulse">
              <div className="h-3 w-24 bg-slate-200 rounded" />
              <div className="mt-3 h-7 w-20 bg-slate-200 rounded" />
            </div>
          ))}
        </div>
      )}
      {meta !== null && meta.total_voters > 0 && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="voter-stats-strip">
          <StatCard label="Voters loaded" value={(meta.total_voters || 0).toLocaleString()} testid="voter-stat-total" />
          <StatCard
            label="Republicans"
            value={(stats?.by_party?.R || 0).toLocaleString()}
            testid="voter-stat-republicans"
          />
          <StatCard
            label="High propensity"
            value={(stats?.by_propensity?.high || 0).toLocaleString()}
            testid="voter-stat-high"
          />
          <StatCard
            label="Tagged"
            value={taggedTotal.toLocaleString()}
            testid="voter-stat-tagged"
          />
        </div>
      )}
      {meta !== null && !meta.total_voters && <EmptyHero onUpload={startUpload} />}

      {/* Filter panel + voter table — also held back until meta has loaded so
          the table area doesn't briefly render the "no voters" empty state.
          Guards instead of a chained ternary: loading OR the grid, never both. */}
      {meta === null && (
        <div
          className="mt-8 bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500"
          data-testid="voter-list-loading"
          role="status"
          aria-live="polite"
        >
          <div className="inline-flex items-center gap-3">
            <span className="h-2 w-2 rounded-full bg-flag-red animate-bounce [animation-delay:-0.32s]" />
            <span className="h-2 w-2 rounded-full bg-flag-red animate-bounce [animation-delay:-0.16s]" />
            <span className="h-2 w-2 rounded-full bg-flag-red animate-bounce" />
            <span className="ml-2 font-display font-bold text-flag-navy">Loading voters…</span>
          </div>
        </div>
      )}
      {meta !== null && meta.total_voters > 0 && (
        <div className="mt-8 grid lg:grid-cols-[320px,1fr] gap-6">
          <aside className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 h-fit lg:sticky lg:top-24">
            <h2 className="font-display text-lg font-bold text-flag-navy flex items-center gap-2">
              <ListFilter className="h-4 w-4" /> Filter
            </h2>
            {/* AI plain-English query */}
            <div className="mt-4 p-3 rounded-lg bg-flag-navy/5 border border-flag-navy/10">
              <Label className="text-xs font-bold uppercase tracking-wider text-flag-navy flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> Describe the list
              </Label>
              <Textarea
                rows={2}
                placeholder="e.g. Republicans over 50 in Canandaigua who voted in 3 of the last 4 elections"
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                data-testid="voter-ai-query-input"
                className="mt-2 text-sm"
              />
              <Button
                onClick={runAi}
                disabled={aiBusy || !aiQuery.trim()}
                data-testid="voter-ai-query-submit"
                className="mt-2 h-9 w-full bg-flag-navy hover:bg-flag-navy-2 text-white font-bold"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                {aiBusy ? "Building…" : "Build filter"}
              </Button>
              {aiExplain && (
                <p className="mt-2 text-xs text-flag-navy/80" data-testid="voter-ai-explain">→ {aiExplain}</p>
              )}
            </div>

            <FilterFields filter={filter} setFilter={setFilter} stats={stats} />
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button onClick={() => applyFilter()} data-testid="voter-apply-filter-btn" className="h-9 bg-flag-red hover:bg-flag-red-hover text-white font-bold">Apply</Button>
              <Button onClick={clearFilter} variant="outline" data-testid="voter-clear-filter-btn" className="h-9 border-2 font-semibold">Clear</Button>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button
                onClick={() => setSaveOpen(true)}
                variant="outline"
                data-testid="voter-save-list-btn"
                className="h-9 border-2 font-semibold"
              >
                <Save className="h-3.5 w-3.5 mr-1.5" /> Save list
              </Button>
              <Button
                onClick={() => exportCsv("filtered")}
                variant="outline"
                data-testid="voter-export-btn"
                className="h-9 border-2 font-semibold"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
              </Button>
            </div>

            {/* Saved lists */}
            {savedLists.length > 0 && (
              <div className="mt-6">
                <h3 className="font-display text-sm font-bold text-flag-navy uppercase tracking-wider">Saved lists</h3>
                <ul className="mt-2 space-y-2" data-testid="voter-saved-lists">
                  {savedLists.map((l) => (
                    <li
                      key={l.id}
                      className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:border-flag-red transition-colors"
                    >
                      <button
                        type="button"
                        onClick={() => applySavedList(l)}
                        data-testid={`voter-saved-list-apply-${l.id}`}
                        className="text-sm font-semibold text-flag-navy text-left truncate flex-1"
                      >
                        {l.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteSavedList(l)}
                        data-testid={`voter-saved-list-delete-${l.id}`}
                        className="text-slate-400 hover:text-flag-red"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>

          {/* Results table */}
          <section>
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-sm text-slate-600" data-testid="voter-result-count">
                {loading ? "Loading…" : (
                  <>Showing <strong className="text-flag-navy">{rows.length}</strong> of <strong className="text-flag-navy">{total.toLocaleString()}</strong> matching voters</>
                )}
              </p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" className="text-flag-red hover:bg-red-50 h-9 font-semibold" data-testid="voter-wipe-btn">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Wipe my voter list
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Wipe your voter list?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently removes every voter and saved list under your account.
                      It does not touch any other account&apos;s data. You&rsquo;ll need to re-upload your file.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="voter-wipe-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        try {
                          await api.delete("/voters?confirm=DELETE");
                          toast.success("Voter list cleared");
                          refreshMeta();
                          fetchRows(1, filter);
                          fetchSavedLists();
                        } catch (err) {
                          toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                        }
                      }}
                      data-testid="voter-wipe-confirm"
                      className="bg-flag-red hover:bg-flag-red-hover"
                    >
                      Wipe everything
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="voter-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                    <tr>
                      <SortableTh col="name" current={sortBy} dir={sortDir} onSort={onSort} testid="voter-th-name" className="px-4 py-3">Voter</SortableTh>
                      <SortableTh col="party" current={sortBy} dir={sortDir} onSort={onSort} testid="voter-th-party">Party</SortableTh>
                      <SortableTh col="age" current={sortBy} dir={sortDir} onSort={onSort} testid="voter-th-age">Age</SortableTh>
                      <SortableTh col="town" current={sortBy} dir={sortDir} onSort={onSort} testid="voter-th-town">Town</SortableTh>
                      <th className="text-left px-3 py-3 font-bold">Phone</th>
                      <SortableTh col="propensity" current={sortBy} dir={sortDir} onSort={onSort} testid="voter-th-propensity">Propensity</SortableTh>
                      <SortableTh col="tag" current={sortBy} dir={sortDir} onSort={onSort} testid="voter-th-tag">Tag</SortableTh>
                      <th className="text-left px-3 py-3 font-bold">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((v) => (
                      <VoterRow key={v.id} voter={v} onChange={patchVoter} onOpenProfile={(voter) => setProfileVoter(voter)} onNoteAdded={handleNoteAdded} />
                    ))}
                    {!loading && rows.length === 0 && (
                      <tr><td colSpan={8} className="text-center py-10 text-slate-500" data-testid="voter-empty-results">
                        No voters match this filter.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination + page size */}
            <div className="mt-3 flex items-center justify-between text-sm flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rows per page</span>
                <Select value={String(pageSize)} onValueChange={onPageSizeChange}>
                  <SelectTrigger className="h-8 w-20 text-xs font-semibold" data-testid="voter-page-size"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[50, 100, 200, 500].map((n) => (<SelectItem key={n} value={String(n)}>{n}</SelectItem>))}
                  </SelectContent>
                </Select>
                <span className="text-slate-500 hidden sm:inline">of <strong className="text-flag-navy">{total.toLocaleString()}</strong> matches</span>
              </div>
              {total > pageSize && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { const np = Math.max(1, page - 1); setPage(np); fetchRows(np, filter, activeBookId, sortBy, sortDir, pageSize); }}
                    disabled={page <= 1 || loading}
                    data-testid="voter-prev-page"
                    className="border-2 font-semibold h-8 px-3"
                  >
                    Previous
                  </Button>
                  <span className="text-slate-600 text-xs">
                    Page <strong className="text-flag-navy">{page}</strong> / {Math.max(1, Math.ceil(total / pageSize))}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => { const np = page + 1; setPage(np); fetchRows(np, filter, activeBookId, sortBy, sortDir, pageSize); }}
                    disabled={rows.length < pageSize || loading}
                    data-testid="voter-next-page"
                    className="border-2 font-semibold h-8 px-3"
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>

            {/* Distribution snapshot */}
            {partyChart.length > 0 && (
              <div className="mt-6 grid sm:grid-cols-3 gap-4">
                <MiniDist title="By party" data={partyChart} labelOf={(k) => k || "Unaffiliated"} />
                <MiniDist title="By propensity" data={propensityChart} labelOf={(k) => k.charAt(0).toUpperCase() + k.slice(1)} colorFor={(k) => PROPENSITY_STYLES[k]} />
                <MiniDist title="By tag" data={tagChart} labelOf={(k) => TAG_OPTIONS.find((o) => o.value === k)?.label || k} colorFor={(k) => TAG_STYLES[k]} />
              </div>
            )}
          </section>
        </div>
      )}

      {/* Privacy modal (accept-once) */}
      <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
        <DialogContent data-testid="voter-privacy-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-flag-red" /> Voter data — handle with care
            </DialogTitle>
            <DialogDescription>
              This is real voters&rsquo; personal information from your board of elections.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2 leading-relaxed">
            <p>By continuing you confirm:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>You obtained this file lawfully from your board of elections (or an equivalent source).</li>
              <li>You will use it only for lawful campaign and civic activity — canvassing, GOTV, voter outreach.</li>
              <li>You will not share, sell, or commercialize it.</li>
              <li>Your data is isolated to your Electable account and never visible to other users.</li>
            </ul>
            <p className="text-xs text-slate-500 pt-2">You can wipe your voter list at any time from this page.</p>
          </div>
          <DialogFooter>
            <Button
              onClick={acceptPrivacy}
              data-testid="voter-privacy-accept"
              className="bg-flag-red hover:bg-flag-red-hover text-white font-bold"
            >
              I understand — continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voter profile dialog — opened by clicking a row */}
      <VoterProfileDialog
        voter={profileVoter}
        busy={profileBusy}
        onClose={() => setProfileVoter(null)}
        onSave={async (patch) => {
          if (!profileVoter) return;
          setProfileBusy(true);
          try {
            const { data } = await api.patch(`/voters/${profileVoter.id}`, patch);
            setRows((prev) => prev.map((r) => (r.id === profileVoter.id ? { ...r, ...data } : r)));
            setProfileVoter((prev) => (prev ? { ...prev, ...data } : prev));
            toast.success("Saved");
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
          } finally {
            setProfileBusy(false);
          }
        }}
      />

      {/* Save list dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="voter-save-list-dialog">
          <DialogHeader>
            <DialogTitle>Save this filter as a list</DialogTitle>
            <DialogDescription>
              Re-apply or export it any time. Saved lists are private to your account.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="save-name" className="text-sm font-semibold">List name</Label>
            <Input
              id="save-name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="e.g. Canandaigua high-prop Republicans"
              className="mt-2 h-11"
              data-testid="voter-save-list-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)} className="border-2 font-semibold">Cancel</Button>
            <Button onClick={saveList} className="bg-flag-red hover:bg-flag-red-hover text-white font-bold" data-testid="voter-save-list-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!importing) setUploadOpen(o); }}>
        <DialogContent data-testid="voter-upload-dialog" className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Upload voter file</DialogTitle>
            <DialogDescription>
              CSV from your county board of elections. Up to 100,000 rows / 25 MB. Your file stays private to your account.
            </DialogDescription>
          </DialogHeader>

          {!parseRes && (
            <div className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center">
              <Upload className="h-8 w-8 text-slate-400 mx-auto" />
              <p className="mt-3 text-slate-600">Drop your CSV here or browse to upload.</p>
              <input
                ref={fileInput}
                type="file"
                accept=".csv,.txt"
                onChange={onPickFile}
                className="hidden"
                data-testid="voter-upload-file-input"
              />
              <Button
                onClick={() => fileInput.current?.click()}
                className="mt-4 h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
                data-testid="voter-upload-browse-btn"
              >
                Choose file
              </Button>
            </div>
          )}

          {parseRes && mapping && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-slate-700">
                  <strong>{uploadFile?.name}</strong> — {parseRes.total_rows.toLocaleString()} rows, {parseRes.columns.length} columns
                </div>
                <Button
                  variant="ghost"
                  onClick={() => { setParseRes(null); setMapping(null); setUploadFile(null); if (fileInput.current) fileInput.current.value = ""; }}
                  className="text-slate-500 hover:text-flag-red font-semibold"
                  data-testid="voter-upload-change-file-btn"
                >
                  Change file
                </Button>
              </div>

              <div>
                <h4 className="font-display font-bold text-flag-navy text-sm mb-2">Map your columns</h4>
                <p className="text-xs text-slate-500 mb-3">
                  Map a <strong>Full name</strong> column, <em>or</em> map both <strong>First name</strong> and <strong>Last name</strong> if your file splits them — most county files do.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <MappingRow label="Full name" req={!(mapping.first_name && mapping.last_name)} value={mapping.name} setValue={(v) => setMapping({ ...mapping, name: v })} options={parseRes.columns} testid="voter-map-name" />
                  <div className="hidden sm:block" />
                  <MappingRow label="First name" req={!mapping.name} value={mapping.first_name} setValue={(v) => setMapping({ ...mapping, first_name: v })} options={parseRes.columns} testid="voter-map-first-name" />
                  <MappingRow label="Last name" req={!mapping.name} value={mapping.last_name} setValue={(v) => setMapping({ ...mapping, last_name: v })} options={parseRes.columns} testid="voter-map-last-name" />
                  <MappingRow label="Party" value={mapping.party} setValue={(v) => setMapping({ ...mapping, party: v })} options={parseRes.columns} testid="voter-map-party" />
                  <MappingRow label="Age" value={mapping.age} setValue={(v) => setMapping({ ...mapping, age: v })} options={parseRes.columns} testid="voter-map-age" />
                  <MappingRow label="Town / city" value={mapping.town} setValue={(v) => setMapping({ ...mapping, town: v })} options={parseRes.columns} testid="voter-map-town" />
                  <MappingRow label="Address" value={mapping.address} setValue={(v) => setMapping({ ...mapping, address: v })} options={parseRes.columns} testid="voter-map-address" />
                  <MappingRow label="Precinct / ward" value={mapping.precinct} setValue={(v) => setMapping({ ...mapping, precinct: v })} options={parseRes.columns} testid="voter-map-precinct" hint="Election district, ward, precinct, or turf — surfaced on every voter card and used to build precinct-scoped walk lists." />
                  <MappingRow label="Phone" value={mapping.phone} setValue={(v) => setMapping({ ...mapping, phone: v })} options={parseRes.columns} testid="voter-map-phone" />
                  <MappingRow label="Absentee / VBM" value={mapping.vbm} setValue={(v) => setMapping({ ...mapping, vbm: v })} options={parseRes.columns} testid="voter-map-vbm" hint="Column with Y/N (or 'Absentee', 'Mail-in') — used for VBM filter and walk-book labeling." />
                  <MappingRow label="State Voter ID" value={mapping.state_voter_id} setValue={(v) => setMapping({ ...mapping, state_voter_id: v })} options={parseRes.columns} testid="voter-map-state-voter-id" hint="Sensitive — stored on the record but never shown in views, exports, or walk books." />
                </div>
              </div>

              <div>
                <h4 className="font-display font-bold text-flag-navy text-sm mb-2">Election history columns</h4>
                <p className="text-xs text-slate-500 mb-2">Pick every column that says whether a voter participated in a given election. We auto-suggested matches.</p>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border border-slate-200 rounded-lg bg-slate-50">
                  {parseRes.columns.map((c) => {
                    const on = mapping.elections.includes(c);
                    return (
                      <button
                        type="button"
                        key={c}
                        onClick={() => toggleElectionCol(c)}
                        data-testid={`voter-map-election-${c}`}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${on ? "bg-flag-red text-white" : "bg-white border-2 border-slate-200 text-slate-700 hover:border-flag-red"}`}
                      >
                        {on && <Check className="h-3 w-3 inline mr-1" />} {c}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <h4 className="font-display font-bold text-flag-navy text-sm mb-2">Mode</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  <ModeCard
                    on={importMode === "replace"}
                    onClick={() => setImportMode("replace")}
                    title="Replace"
                    testid="voter-mode-replace"
                    desc="Wipe your existing voter list and start fresh with this file. Use this on the first upload or when you've gotten a brand-new file from your county."
                  />
                  <ModeCard
                    on={importMode === "append"}
                    onClick={() => setImportMode("append")}
                    title="Append (merge)"
                    testid="voter-mode-append"
                    desc="Add new voters and refresh facts on existing ones. Your support tags and notes are preserved on every voter you've already tagged."
                  />
                </div>
                {importMode === "replace" && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span><strong>Heads up:</strong> Replace mode deletes all existing tags and notes you&rsquo;ve applied. Pick Append if you want to keep them.</span>
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-display font-bold text-flag-navy text-sm mb-2">Preview</h4>
                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        {parseRes.columns.slice(0, 8).map((c) => (
                          <th key={c} className="text-left px-2 py-1.5 font-semibold text-slate-600">{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parseRes.preview_rows.map((r, i) => (
                        <tr key={`pr-${i}`} className="border-t border-slate-100">
                          {r.slice(0, 8).map((c, j) => (
                            <td key={`pc-${i}-${j}`} className="px-2 py-1.5 text-slate-700 whitespace-nowrap">{c}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={importing} className="border-2 font-semibold">
              Cancel
            </Button>
            <Button
              onClick={commitImport}
              disabled={importing || !parseRes || !(mapping?.name || (mapping?.first_name && mapping?.last_name))}
              data-testid="voter-upload-commit-btn"
              className="bg-flag-red hover:bg-flag-red-hover text-white font-bold"
            >
              {importCommitLabel(importing, importMode)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Sub-components (kept small and inline for one-file simplicity) ---------- */

function StatCard({ label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm">
      <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="font-display text-2xl font-black text-flag-navy mt-1">{value}</div>
    </div>
  );
}

function EmptyHero({ onUpload }) {
  return (
    <div className="mt-8 bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center" data-testid="voter-empty-hero">
      <Users className="h-10 w-10 text-slate-400 mx-auto" />
      <h2 className="font-display text-2xl font-bold text-flag-navy mt-3">No voter file yet</h2>
      <p className="text-slate-600 mt-2 max-w-xl mx-auto">
        Upload the voter file you got from your county board of elections. We&rsquo;ll walk you through mapping your columns and tagging supporters as you canvass.
      </p>
      <Button
        onClick={onUpload}
        className="mt-5 h-11 px-5 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
        data-testid="voter-empty-upload-btn"
      >
        <Upload className="h-4 w-4 mr-1.5" /> Upload voter file
      </Button>
      <p className="text-xs text-slate-500 mt-4 flex items-center justify-center gap-1.5">
        <FileWarning className="h-3 w-3" /> Electable never sources or provides voter data — bring your own.
      </p>
    </div>
  );
}

function FilterFields({ filter, setFilter, stats }) {
  const maxElections = stats?.elections_total || 0;
  return (
    <div className="mt-4 space-y-3">
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
          <Search className="h-3 w-3" /> Search
        </Label>
        <Input
          value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}
          placeholder="Name, address, town, phone…"
          className="mt-1 h-10"
          data-testid="voter-filter-search"
        />
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Party</Label>
        <Select
          value={filter.party === "" ? "__unaffiliated" : (filter.party || "any")}
          onValueChange={(v) => setFilter({ ...filter, party: v === "__unaffiliated" ? "" : v })}
        >
          <SelectTrigger data-testid="voter-filter-party" className="mt-1 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PARTY_OPTIONS.map((o) => (
              <SelectItem
                key={o.value === "" ? "__unaffiliated" : (o.value || "any")}
                value={o.value === "" ? "__unaffiliated" : (o.value || "any")}
              >{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Town contains</Label>
        <Input
          value={filter.town}
          onChange={(e) => setFilter({ ...filter, town: e.target.value })}
          placeholder="e.g. Canandaigua"
          className="mt-1 h-10"
          data-testid="voter-filter-town"
        />
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Precinct / ward</Label>
        <Input
          value={filter.precinct}
          onChange={(e) => setFilter({ ...filter, precinct: e.target.value })}
          placeholder="e.g. 3-2 or Ward 7"
          className="mt-1 h-10"
          data-testid="voter-filter-precinct"
        />
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
          <Mail className="h-3 w-3" /> Absentee / Vote-by-mail
        </Label>
        <Select value={filter.vbm || "any"} onValueChange={(v) => setFilter({ ...filter, vbm: v })}>
          <SelectTrigger data-testid="voter-filter-vbm" className="mt-1 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any (in-person or VBM)</SelectItem>
            <SelectItem value="yes">VBM / absentee only</SelectItem>
            <SelectItem value="no">Non-VBM only</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <label className="flex items-center gap-2 select-none cursor-pointer" data-testid="voter-filter-has-notes-row">
        <input
          type="checkbox"
          checked={!!filter.has_notes}
          onChange={(e) => setFilter({ ...filter, has_notes: e.target.checked })}
          className="h-4 w-4 accent-flag-red"
          data-testid="voter-filter-has-notes"
        />
        <span className="text-xs font-semibold text-slate-700">
          Has notes <span className="text-slate-400 font-normal">— voters you&rsquo;ve already spoken with</span>
        </span>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Age ≥</Label>
          <Input type="number" min="0" value={filter.age_min} onChange={(e) => setFilter({ ...filter, age_min: e.target.value })} className="mt-1 h-10" data-testid="voter-filter-age-min" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Age ≤</Label>
          <Input type="number" min="0" value={filter.age_max} onChange={(e) => setFilter({ ...filter, age_max: e.target.value })} className="mt-1 h-10" data-testid="voter-filter-age-max" />
        </div>
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tags</Label>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {TAG_FILTER_PILLS.map((o) => {
            const on = filter.tags.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => setFilter({ ...filter, tags: on ? filter.tags.filter((t) => t !== o.value) : [...filter.tags, o.value] })}
                data-testid={`voter-filter-tag-${o.value}`}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${on ? "bg-flag-red text-white" : "bg-white border-2 border-slate-200 text-slate-700 hover:border-flag-red"}`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Propensity</Label>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {PROPENSITY_OPTIONS.map((o) => {
            const on = filter.propensity.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => setFilter({ ...filter, propensity: on ? filter.propensity.filter((t) => t !== o.value) : [...filter.propensity, o.value] })}
                data-testid={`voter-filter-propensity-${o.value}`}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${on ? "bg-flag-navy text-white" : "bg-white border-2 border-slate-200 text-slate-700 hover:border-flag-navy"}`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Voted in ≥ N of last M elections</Label>
        <div className="mt-1 grid grid-cols-2 gap-2">
          <Input type="number" min="0" placeholder="N" value={filter.voted_min} onChange={(e) => setFilter({ ...filter, voted_min: e.target.value })} className="h-10" data-testid="voter-filter-voted-min" />
          <Input type="number" min="0" placeholder={maxElections ? `M (max ${maxElections})` : "M"} value={filter.recent_window} onChange={(e) => setFilter({ ...filter, recent_window: e.target.value })} className="h-10" data-testid="voter-filter-recent-window" />
        </div>
      </div>
    </div>
  );
}

function MappingRow({ label, req, value, setValue, options, testid, hint }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
        {label}{req && <span className="text-flag-red ml-1">*</span>}
      </Label>
      <Select value={value || "__none"} onValueChange={(v) => setValue(v === "__none" ? "" : v)}>
        <SelectTrigger data-testid={testid} className="mt-1 h-10"><SelectValue placeholder="— Not mapped —" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none">— Not mapped —</SelectItem>
          {options.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
      {hint && <p className="text-[10.5px] text-slate-500 mt-1 leading-snug">{hint}</p>}
    </div>
  );
}

function ModeCard({ on, onClick, title, desc, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left border-2 rounded-xl px-4 py-3 transition-colors ${on ? "border-flag-red bg-flag-red/5" : "border-slate-200 hover:border-slate-300"}`}
    >
      <div className="font-display font-bold text-flag-navy">{title}</div>
      <p className="text-xs text-slate-600 mt-1 leading-relaxed">{desc}</p>
    </button>
  );
}

function fmtNoteDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

function VoterRow({ voter, onChange, onOpenProfile, onNoteAdded }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [draftNote, setDraftNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const noteInputRef = useRef(null);
  const notes = Array.isArray(voter.notes) ? voter.notes : [];
  const latest = notes[0];  // server sorts desc
  const tag = voter.tag || "";

  const saveNote = async () => {
    const text = draftNote.trim();
    if (!text || savingNote) return;
    setSavingNote(true);
    try {
      const { data: note } = await api.post(`/voters/${voter.id}/notes`, { text });
      onNoteAdded && onNoteAdded(voter, note);
      setDraftNote("");
      // Feature #3: keep the box open so the next note is one tap away.
      setTimeout(() => noteInputRef.current?.focus(), 30);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`voter-row-${voter.id}`}>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={() => onOpenProfile && onOpenProfile(voter)}
          data-testid={`voter-row-open-${voter.id}`}
          className="text-left group"
          title="Open voter profile"
        >
          <div className="font-semibold text-flag-navy group-hover:text-flag-red transition-colors">{voter.name}</div>
          {voter.address && <div className="text-xs text-slate-500 mt-0.5">{voter.address}</div>}
          {voter.precinct && (
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-0.5" data-testid={`voter-row-precinct-${voter.id}`}>
              Precinct {voter.precinct}
            </div>
          )}
        </button>
      </td>
      <td className="px-3 py-3 text-slate-700">{voter.party_raw || voter.party || "—"}</td>
      <td className="px-3 py-3 text-slate-700">{voter.age ?? "—"}</td>
      <td className="px-3 py-3 text-slate-700">{voter.town || "—"}</td>
      <td className="px-3 py-3 text-slate-700">
        {voter.phone ? (
          <a
            href={`tel:${voter.phone.replace(/[^0-9+]/g, "")}`}
            data-testid={`voter-row-phone-${voter.id}`}
            className="text-flag-navy hover:text-flag-red font-medium whitespace-nowrap"
          >
            {voter.phone}
          </a>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-3">
        <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${PROPENSITY_STYLES[voter.propensity] || ""}`}>
          {voter.propensity}{" "}
          <span className="text-slate-500 font-medium ml-1 normal-case tracking-normal">({voter.participated_count}/{voter.elections_total})</span>
        </span>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-1">
          <Select
            value={tag || "__none"}
            onValueChange={(v) => onChange(voter, { tag: v === "__none" ? "" : v })}
          >
            <SelectTrigger data-testid={`voter-row-tag-${voter.id}`} className={`h-8 text-xs font-semibold flex-1 min-w-0 ${tag && TAG_STYLES[tag] ? `${TAG_STYLES[tag]} border-transparent` : ""}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TAG_OPTIONS.map((o) => (
                <SelectItem key={o.value || "__none"} value={o.value || "__none"}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Feature #4: one-tap "No Answer" — the most common canvassing result. */}
          {tag !== "no_answer" && (
            <button
              type="button"
              onClick={() => onChange(voter, { tag: "no_answer" })}
              className="text-[10px] font-bold uppercase tracking-wider bg-orange-100 hover:bg-orange-200 text-orange-900 px-2 py-1 rounded whitespace-nowrap"
              data-testid={`voter-row-no-answer-${voter.id}`}
              title="Mark as No Answer"
            >
              No ans
            </button>
          )}
        </div>
      </td>
      <td className="px-3 py-3 align-top min-w-[16rem]">
        {notes.length > 0 && (
          <div className="mb-2 space-y-1" data-testid={`voter-row-notes-list-${voter.id}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              {notes.length} note{notes.length === 1 ? "" : "s"}
            </div>
            <div className="text-xs text-slate-700 leading-relaxed">
              <span className="text-slate-400 text-[10px] font-semibold mr-1">{fmtNoteDate(latest.created_at)}</span>
              <span className="italic">{latest.text.length > 90 ? latest.text.slice(0, 90) + "…" : latest.text}</span>
            </div>
            {notes.length > 1 && (
              <button
                type="button"
                onClick={() => onOpenProfile && onOpenProfile(voter)}
                className="text-[10px] font-bold uppercase tracking-wider text-flag-navy hover:text-flag-red"
              >
                See all {notes.length}
              </button>
            )}
          </div>
        )}
        {!noteOpen ? (
          <button
            type="button"
            onClick={() => { setNoteOpen(true); setTimeout(() => noteInputRef.current?.focus(), 30); }}
            data-testid={`voter-row-note-open-${voter.id}`}
            className="text-[11px] font-semibold text-flag-navy hover:text-flag-red inline-flex items-center gap-1"
          >
            <Plus className="h-3 w-3" /> {notes.length === 0 ? "Add a note…" : "Add another note"}
          </button>
        ) : (
          <div className="space-y-1.5">
            <Textarea
              ref={noteInputRef}
              rows={2}
              value={draftNote}
              onChange={(e) => setDraftNote(e.target.value)}
              onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); saveNote(); } }}
              placeholder="Door-knock, phone call, follow-up…"
              data-testid={`voter-row-note-input-${voter.id}`}
              className="text-xs"
            />
            <div className="flex items-center gap-1">
              <Button
                onClick={saveNote}
                disabled={!draftNote.trim() || savingNote}
                data-testid={`voter-row-note-save-${voter.id}`}
                className="h-7 px-2 text-xs bg-flag-red hover:bg-flag-red-hover text-white font-bold"
              >
                {savingNote ? "Saving…" : "Save"}
              </Button>
              <Button variant="ghost" onClick={() => { setDraftNote(""); setNoteOpen(false); }} className="h-7 px-2 text-xs">Done</Button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}

function MiniDist({ title, data, labelOf, colorFor }) {
  const total = data.reduce((s, [, n]) => s + n, 0) || 1;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <h3 className="font-display text-sm font-bold text-flag-navy uppercase tracking-wider">{title}</h3>
      <ul className="mt-2 space-y-1.5">
        {data.map(([k, n]) => (
          <li key={k} className="flex items-center justify-between gap-2 text-sm">
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colorFor ? colorFor(k) : "bg-slate-100 text-slate-700"}`}>{labelOf(k)}</span>
            <span className="text-slate-700 font-semibold">{n.toLocaleString()}</span>
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-flag-navy" style={{ width: `${(n / total) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}



// Clickable column header that toggles asc/desc. Visual cue:
//   - inactive column → faint up/down stack
//   - active asc → up chevron
//   - active desc → down chevron
function SortableTh({ col, current, dir, onSort, testid, className, children }) {
  const active = current === col;
  let Icon = ChevronsUpDown;
  let ariaSort = "none";
  if (active) {
    Icon = dir === "asc" ? ChevronUp : ChevronDown;
    ariaSort = dir === "asc" ? "ascending" : "descending";
  }
  const base = "text-left px-3 py-3 font-bold cursor-pointer select-none hover:bg-slate-100 transition-colors";
  return (
    <th
      onClick={() => onSort(col)}
      data-testid={testid}
      aria-sort={ariaSort}
      className={`${base} ${className || ""}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <Icon className={`h-3 w-3 ${active ? "text-flag-red" : "text-slate-400"}`} />
      </span>
    </th>
  );
}

// Per-voter full-profile dialog. Opens when a row is clicked. Pulls fresh
// detail from the backend (so values reflect any concurrent edit), and lets
// the owner edit `tag` and `notes` inline. Honors the state-voter-id privacy
// rule: that field is stripped server-side and never displayed here.
function VoterProfileDialog({ voter, busy, onClose, onSave }) {
  const [detail, setDetail] = useState(null);
  const [draftTag, setDraftTag] = useState("");
  const [draftNote, setDraftNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const noteInputRef = useRef(null);

  useEffect(() => {
    if (!voter) { setDetail(null); return; }
    let cancelled = false;
    api.get(`/voters/${voter.id}`)
      .then(({ data }) => {
        if (cancelled) return;
        setDetail(data);
        setDraftTag(data.tag || "");
      })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message));
    return () => { cancelled = true; };
    // Re-fetch only when the voter identity changes. The setters and toast
    // helper are stable references from React / an outer module.
  }, [voter]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!voter) return null;
  const v = detail || voter;
  const notes = Array.isArray(v.notes) ? v.notes : [];
  const dirty = (draftTag || "") !== (v.tag || "");
  const elections = Array.isArray(v.elections) ? v.elections : [];

  const saveNote = async () => {
    const text = draftNote.trim();
    if (!text || savingNote) return;
    setSavingNote(true);
    try {
      const { data: note } = await api.post(`/voters/${v.id}/notes`, { text });
      // Prepend to the local list — server sort is desc.
      setDetail((prev) => prev ? { ...prev, notes: [note, ...(prev.notes || [])] } : prev);
      setDraftNote("");
      // Feature #3: keep the input ready for the next note.
      setTimeout(() => noteInputRef.current?.focus(), 30);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/voters/${v.id}/notes/${noteId}`);
      setDetail((prev) => prev ? { ...prev, notes: (prev.notes || []).filter((n) => n.id !== noteId) } : prev);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <Dialog open={!!voter} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid={`voter-profile-${voter.id}`}>
        <DialogHeader>
          <DialogTitle data-testid="voter-profile-name" className="text-flag-navy font-display text-2xl font-black">
            {v.name}
          </DialogTitle>
          <DialogDescription>
            {[v.address, v.town].filter(Boolean).join(", ") || "Address on file"}
            {v.precinct && (
              <span className="ml-2 inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700" data-testid="voter-profile-precinct">
                Precinct {v.precinct}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {!detail ? <div className="text-slate-500 text-sm py-6">Loading…</div> : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Quick-facts grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="voter-profile-facts">
              <Fact label="Party" value={v.party_raw || v.party || "—"} />
              <Fact label="Age" value={v.age ?? "—"} />
              <Fact label="Town" value={v.town || "—"} />
              <Fact label="Precinct" value={v.precinct || <span className="text-slate-400">—</span>} />
              <Fact
                label="Phone"
                value={v.phone ? (
                  <a href={`tel:${(v.phone || "").replace(/[^0-9+]/g, "")}`} className="text-flag-navy hover:text-flag-red font-semibold">{v.phone}</a>
                ) : "—"}
              />
              <Fact label="VBM / Absentee" value={v.vbm ? <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-flag-navy text-white">Yes</span> : <span className="text-slate-500 text-xs">No</span>} />
              <Fact
                label="Propensity"
                value={
                  <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${PROPENSITY_STYLES[v.propensity] || ""}`}>
                    {v.propensity || "—"} ({v.participated_count ?? 0}/{v.elections_total ?? 0})
                  </span>
                }
              />
            </div>

            {/* Voting history */}
            <div>
              <h4 className="font-display text-xs font-bold uppercase tracking-wider text-slate-600">Voting history</h4>
              {elections.length === 0 ? (
                <p className="text-sm text-slate-500 mt-1">No election participation recorded.</p>
              ) : (
                <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="voter-profile-elections">
                  {elections.map((e) => (
                    <li key={e} className="inline-flex items-center text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                      <Check className="h-2.5 w-2.5 mr-1" /> {e}
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-1 text-[11px] text-slate-500">
                Out of {v.elections_total ?? 0} elections tracked in this list.
              </p>
            </div>

            {/* Tag editor */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Support tag</Label>
              <Select value={draftTag || "__none"} onValueChange={(val) => setDraftTag(val === "__none" ? "" : val)}>
                <SelectTrigger
                  data-testid="voter-profile-tag"
                  className={`mt-1 h-10 font-semibold ${draftTag && TAG_STYLES[draftTag] ? `${TAG_STYLES[draftTag]} border-transparent` : ""}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAG_OPTIONS.map((o) => (
                    <SelectItem key={o.value || "__none"} value={o.value || "__none"}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes: running log per voter, most-recent first */}
            <div>
              <div className="flex items-baseline justify-between">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Notes ({notes.length})</Label>
                <span className="text-[10.5px] text-slate-500">Auto-dated · newest first · private to your account</span>
              </div>
              <div className="mt-2 space-y-2" data-testid="voter-profile-notes-list">
                {notes.length === 0 && (
                  <p className="text-[11px] italic text-slate-500">No notes yet. Every note is timestamped automatically when you save.</p>
                )}
                {notes.map((n) => (
                  <div key={n.id} className="bg-slate-50 border border-slate-200 rounded-lg p-2.5" data-testid={`voter-profile-note-${n.id}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{fmtNoteDate(n.created_at)}</span>
                      <button
                        type="button"
                        onClick={() => deleteNote(n.id)}
                        className="text-[10px] font-semibold text-slate-400 hover:text-flag-red"
                        title="Delete this note"
                        data-testid={`voter-profile-note-delete-${n.id}`}
                      >
                        Delete
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{n.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 space-y-2">
                <Textarea
                  ref={noteInputRef}
                  rows={3}
                  value={draftNote}
                  onChange={(e) => setDraftNote(e.target.value)}
                  onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); saveNote(); } }}
                  placeholder="Door-knock, phone call, follow-up… (⌘/Ctrl + Enter to save)"
                  data-testid="voter-profile-note-input"
                />
                <Button
                  onClick={saveNote}
                  disabled={!draftNote.trim() || savingNote}
                  data-testid="voter-profile-note-save"
                  className="h-9 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
                >
                  {savingNote ? "Saving…" : "Add note"}
                </Button>
              </div>
            </div>

            <p className="text-[11px] italic text-slate-500" data-testid="voter-profile-privacy">
              State voter ID is stored for deduplication but never displayed in profiles, exports, or walk books.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="voter-profile-close">Close</Button>
          <Button
            onClick={() => onSave({ tag: draftTag })}
            disabled={!dirty || busy}
            data-testid="voter-profile-save"
            className="bg-flag-red hover:bg-flag-red-hover text-white font-bold"
          >
            {busy ? "Saving…" : "Save tag"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Fact({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-semibold text-flag-navy text-sm mt-0.5">{value}</div>
    </div>
  );
}
